<script setup lang="ts">
import { type PropType, computed } from 'vue'
import { toast } from 'vue-sonner'
import { getCouponEventExtra } from '@/lib/utils'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import {
	type DiscountTypeValue,
	type ApiOrderTypeValue,
	ApiOrderType,
	DiscountType,
} from '@/types/Coupon'
import BaseButton from './ui/button/BaseButton.vue'
import BaseIcon from './ui/icon/BaseIcon.vue'
import Skeleton from './Skeleton.vue'

defineOptions({
	inheritAttrs: false,
})

const props = defineProps({
	type: {
		type: String as PropType<'coupon' | 'mercari-campaign'>,
		default: 'coupon',
	},
	orderType: {
		type: String as PropType<ApiOrderTypeValue>,
		default: ApiOrderType.Shipping,
	},
	discountType: {
		type: String as PropType<DiscountTypeValue>,
		default: DiscountType.FIXED,
	},
	title: {
		type: String,
		required: true,
		default: '',
	},
	couponCode: {
		type: String,
		required: true,
		default: '',
	},
	couponAmount: {
		type: [Number, null] as PropType<number | null>,
		required: true,
		default: null,
	},
	percentageDiscount: {
		type: [Number, null] as PropType<number | null>,
		required: true,
		default: null,
	},
	startDate: {
		type: [String, null] as PropType<string | null>,
		required: true,
		default: null,
	},
	expiryDate: {
		type: String,
		required: true,
		default: '',
	},
	isExpireSoon: {
		type: Boolean,
		required: true,
		default: false,
	},
	terms: {
		type: String,
		required: true,
		default: '',
	},
	isDisabled: {
		type: Boolean,
		default: false,
	},
	isInModal: {
		type: Boolean,
		default: false,
	},
	text: {
		type: Object,
		default: null,
	},
	currentPageName: {
		type: String,
		default: '',
	},
})

const emit = defineEmits(['showModal'])

const displayedAmount = computed(() => {
	if (props.discountType === DiscountType.FIXED && props.couponAmount) {
		const formattedResult = `${props.couponAmount.toLocaleString('en', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		})}`

		return props.text.coupon_card_amount.replace('{amount}', formattedResult)
	}
	if (props.discountType === DiscountType.PERCENTAGE) {
		return `${props.percentageDiscount}%`
	}
	if (props.discountType === DiscountType.WAIVE_SERVICE_FEE) {
		return '0%'
	}

	return ''
})

const displayedAmountSuffix = computed(() => {
	if (props.discountType === DiscountType.PERCENTAGE) {
		return props.text.coupon_card_percentage_off
	}
	if (props.discountType === DiscountType.WAIVE_SERVICE_FEE) {
		return props.text.coupon_card_shipment_unit
	}

	return ''
})

const displayedExpiryDate = computed(() => {
	return props.text.coupon_card_expiry_date_description.replace(
		'{date}',
		props.expiryDate,
	)
})

const displayedOrderType = computed(() => {
	if (props.orderType === ApiOrderType.ExpressCheckout) {
		return props.text.coupon_card_service_type_buyandshop_title
	}
	if (props.orderType === ApiOrderType.ProxyShopping) {
		return props.text.coupon_card_service_type_buyforyou_title
	}
	if (props.orderType === ApiOrderType.Shipping) {
		return props.text.coupon_card_service_type_buyandship_title
	}

	return ''
})

const handleCopyCouponCode = () => {
	toast.success(props.text.coupon_card_copy_success)
	navigator.clipboard.writeText(props.couponCode)

	const pageName = props.currentPageName

	const buttonName = props.isInModal
		? 'Copy Coupon Code-Detail'
		: 'Copy Coupon Code'
	postButtonClickGAEvent(buttonName, pageName)
}

const handleDetailsClick = () => {
	emit('showModal')

	postButtonClickGAEvent('Show Coupon Detail')
}

const couponEventExtra = computed(() => {
	return getCouponEventExtra(props.orderType, props.couponCode)
})

const postButtonClickGAEvent = (
	buttonName: string,
	pageName: string = props.currentPageName,
) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: pageName,
		button_name: buttonName,
		event_extra: couponEventExtra.value,
	})
}
</script>

<template>
	<div
		class="group flex h-[140px] w-[335px] flex-grow cursor-pointer bg-o-50 mdp:w-[386px] [&.disabled_*]:text-n-400 [@media(min-width:375px)]:w-[350px]"
		:class="{ disabled: isDisabled }"
		@click="handleDetailsClick"
	>
		<div
			class="flex w-full flex-1 flex-col overflow-hidden rounded-l-[8px] rounded-t-[8px] rounded-tr-none border border-b border-r-0 border-o-500 bg-o-50"
		>
			<div
				class="typography-c2-reg w-min text-ellipsis whitespace-nowrap rounded-bl-none rounded-br-[8px] bg-o-75 px-16px py-4px text-o-500 group-[.disabled]:bg-n-50"
			>
				{{ displayedOrderType }}
			</div>

			<div
				class="my-auto flex flex-col items-start justify-center gap-8px p-12px pt-8px"
			>
				<div class="typography-s1-str line-clamp-2 text-left">
					{{ displayedAmount }}
					{{ displayedAmountSuffix }}
					{{ title }}
				</div>
				<div class="typography-c2-reg line-clamp-1 text-n-500">
					{{ displayedExpiryDate }}
				</div>

				<BaseIcon name="info_v2" class="fill-n-500" />
			</div>
		</div>

		<!-- coupon divider -->
		<div class="flex flex-col items-center justify-center">
			<div class="relative h-[9px] w-[18px] rotate-180 overflow-hidden">
				<div
					class="absolute h-[18px] w-[18px] rounded-full border-[1px] border-o-500 bg-white"
				></div>
			</div>
			<!-- divider -->
			<div class="h-full w-full p-8px">
				<div
					class="h-full w-[1px] bg-[linear-gradient(to_bottom,#F56A00_4px,transparent_4px)] bg-[length:1px_8px] bg-repeat-y"
				></div>
			</div>
			<div class="relative h-[9px] w-[18px] overflow-hidden">
				<div
					class="absolute h-[18px] w-[18px] rounded-full border-[1px] border-o-500 bg-white"
				></div>
			</div>
		</div>

		<!-- right card content -->
		<div
			class="w-[120px] rounded-b-[8px] rounded-l-none rounded-tr-[8px] border border-l-0 border-t border-o-500"
		>
			<div
				class="flex h-full w-full flex-col items-center justify-center gap-12px p-12px text-o-500 group-[.disabled_*]:text-n-300"
			>
				<div class="typography-s1-str line-clamp-2 max-w-[96px] text-center">
					{{ couponCode }}
				</div>
				<BaseButton
					variant="primary"
					size="small"
					@click.stop="handleCopyCouponCode"
				>
					{{ text.coupon_card_copy }}
				</BaseButton>
			</div>
		</div>
	</div>
</template>
